import type { CalendarEvent } from '../types.js';
import type {
  CalendarEventActionPermissions,
} from '../calendar-view-props.js';
import type { CalendarMessages } from '../i18n.js';
import type { EventMenuActions } from './eventMenu.js';

export type CalendarEventActionKey = keyof CalendarEventActionPermissions;

export function eventActionAllowed(
  permissions: CalendarEventActionPermissions,
  key: CalendarEventActionKey,
): boolean {
  return permissions[key] !== false;
}

export interface BuildEventContextMenuInput {
  readonly hasAnyContextAction: boolean;
  readonly messages: CalendarMessages;
  readonly actionAllowed: (event: CalendarEvent, key: CalendarEventActionKey) => boolean;
  readonly onSelectEvent?: ((event: CalendarEvent) => void) | undefined;
  readonly onEditEvent?: ((event: CalendarEvent) => void) | undefined;
  readonly onDuplicateEvent?: ((event: CalendarEvent) => void) | undefined;
  readonly onCancelEvent?: ((event: CalendarEvent) => void) | undefined;
  readonly onDeleteEvent?: ((event: CalendarEvent) => void) | undefined;
}

export function buildCalendarEventContextMenu(
  input: BuildEventContextMenuInput,
): ((event: CalendarEvent) => EventMenuActions) | undefined {
  if (!input.hasAnyContextAction) return undefined;
  return (event: CalendarEvent): EventMenuActions => {
    const canOpen = input.actionAllowed(event, 'canOpen');
    const canEdit = input.actionAllowed(event, 'canEdit');
    const canDuplicate = input.actionAllowed(event, 'canDuplicate');
    const canDelete = input.actionAllowed(event, 'canDelete');
    const canUpdate = input.actionAllowed(event, 'canUpdate');
    return {
      labels: {
        open: input.messages.eventMenuOpen,
        edit: input.messages.eventMenuEdit,
        duplicate: input.messages.eventMenuDuplicate,
        moveToToday: input.messages.eventMenuMoveToToday,
        uncancel: input.messages.eventMenuUncancel,
        cancel: input.messages.eventMenuCancel,
        delete: input.messages.eventMenuDelete,
      },
      ...(input.onSelectEvent !== undefined && canOpen
        ? { onOpen: input.onSelectEvent }
        : {}),
      ...(input.onEditEvent !== undefined && canEdit
        ? { onEdit: input.onEditEvent }
        : input.onSelectEvent !== undefined && canEdit
          ? { onEdit: input.onSelectEvent }
          : {}),
      ...(input.onDuplicateEvent !== undefined && canDuplicate
        ? { onDuplicate: input.onDuplicateEvent }
        : {}),
      ...(input.onCancelEvent !== undefined && canUpdate
        ? { onCancel: input.onCancelEvent }
        : {}),
      ...(input.onDeleteEvent !== undefined && canDelete
        ? { onDelete: input.onDeleteEvent }
        : {}),
    };
  };
}
